import type { MeResponse } from './types'
import { request } from './client'

export interface AuthProviders {
    google: boolean
    apple: boolean
    password: boolean
}

export function me(): Promise<MeResponse> {
    return request<MeResponse>('/api/me')
}

export function providers(): Promise<AuthProviders> {
    return request<AuthProviders>('/auth/providers')
}

/// Password login never returns a session directly — two-factor is
/// mandatory. Enrolled accounts get `mfa_required` (finish with
/// `loginTotp`); un-enrolled accounts get `mfa_enroll_required` (finish
/// with `enrollTotpSetup` + `enrollTotpConfirm`).
export interface LoginResponse {
    ok?: boolean
    mfa_required?: boolean
    mfa_enroll_required?: boolean
    challenge?: string
}

export function login(email: string, password: string): Promise<LoginResponse> {
    return request('/auth/login', { method: 'POST', json: { email, password } })
}

export function loginTotp(challenge: string, code: string): Promise<{ ok: boolean }> {
    return request('/auth/login/totp', { method: 'POST', json: { challenge, code } })
}

export function enrollTotpSetup(
    challenge: string,
): Promise<{ secret: string; otpauth_url: string }> {
    return request('/auth/enroll-totp/setup', { method: 'POST', json: { challenge } })
}

export function enrollTotpConfirm(
    challenge: string,
    code: string,
): Promise<{ ok: boolean; backup_codes: string[] }> {
    return request('/auth/enroll-totp/confirm', { method: 'POST', json: { challenge, code } })
}

export function register(payload: {
    email: string
    password: string
    display_name?: string
}): Promise<{ ok: boolean; verify_required?: boolean }> {
    return request('/auth/register', { method: 'POST', json: payload })
}

export function logout(): Promise<{ ok: boolean }> {
    return request('/auth/logout', { method: 'POST' })
}

// ── Email verification & password recovery ───────────────────────────

export function verifyEmail(token: string): Promise<{ ok: boolean }> {
    return request('/auth/verify-email', { method: 'POST', json: { token } })
}

export function resendVerification(email: string): Promise<{ ok: boolean }> {
    return request('/auth/resend-verification', { method: 'POST', json: { email } })
}

export function forgotPassword(email: string): Promise<{ ok: boolean }> {
    return request('/auth/forgot-password', { method: 'POST', json: { email } })
}

export function resetPassword(token: string, password: string): Promise<{ ok: boolean }> {
    return request('/auth/reset-password', { method: 'POST', json: { token, password } })
}

// ── TOTP two-factor management (signed-in) ───────────────────────────

export interface TotpStatus {
    enabled: boolean
    pending: boolean
    backup_codes_left: number
}

export function totpStatus(): Promise<TotpStatus> {
    return request('/auth/totp')
}

export function totpSetup(): Promise<{ secret: string; otpauth_url: string }> {
    return request('/auth/totp/setup', { method: 'POST' })
}

export function totpConfirm(code: string): Promise<{ ok: boolean; backup_codes: string[] }> {
    return request('/auth/totp/confirm', { method: 'POST', json: { code } })
}

export function totpDisable(code: string): Promise<{ ok: boolean }> {
    return request('/auth/totp', { method: 'DELETE', json: { code } })
}
